'use client';

import React, { useEffect, useState } from 'react';

import { useTrack } from '@blockworks/platform/services/analytics';

import SingleSelectDropdown, { SingleSelectInputOption } from '@/components/display/select/single-select-dropdown';
import GrantsHeader from '@/components/pages/grants/components/grants-header';
import GrantsPopupDialog, { DialogData } from '@/components/pages/grants/components/grants-popup-dialog';
import IndividualGrantsRow from '@/components/pages/grants/components/individual-grants-row';
import SortChevron from '@/components/pages/grants/components/sort-chevron';
import NewsRecirculationSection from '@/components/sections/content/news-recirculation-section';
import { GrantsEntities, IndividualGrant } from '@/types/pages/api/grants/grants-entities';
import useSortableData from '@/utils/hooks/use-sortable-data';

type Props = {
    individualGrants: IndividualGrant[];
    ecosystems: GrantsEntities['ecosystemsById'];
    categories: SingleSelectInputOption[];
    grantsTypes: SingleSelectInputOption[];
    selectedProgram: string | null;
    title: string;
    counts?: number[];
};

const GrantsPageLayout = (props: Props) => {
    const { title, selectedProgram, ecosystems, grantsTypes, categories, individualGrants, counts } = props;
    const track = useTrack();
    const [searchTerm, setSearchTerm] = useState('');
    const [, setPage] = useState(0);
    const [selectedEcosysstem, setSelectedEcosystem] = useState<SingleSelectInputOption | null>(null);
    const [filteredGrants, setFilteredGrants] = useState<IndividualGrant[]>(individualGrants);
    const [selectedGrantsType, setSelectedGrantsType] = useState<SingleSelectInputOption | null>(null);
    const [selectedProjectCategory, setSelectedProjectCategory] = useState<SingleSelectInputOption | null>(null);
    const [selectedReward, setSelectedReward] = useState<SingleSelectInputOption | null>(null);
    const [dialogData, setDialogData] = useState<DialogData | null>(null);
    const {
        items: sortedAndFiltered,
        requestSort,
        sortConfig,
    } = useSortableData<IndividualGrant>(filteredGrants, {
        direction: 'asc',
        key: 'title',
    });

    const ecosystemOptions: SingleSelectInputOption[] = React.useMemo(() => {
        return individualGrants
            .map(g => g?.project?.ecosystem.map(eco => eco.replace('0 - ', '')))
            .flat()
            .filter((value, index, self) => index === self.findIndex(t => t === value) && ecosystems[value!])
            .map<SingleSelectInputOption>(val => ecosystems[val!]!);
    }, [individualGrants, ecosystems]);

    useEffect(() => {
        let grantsFilter = individualGrants;

        if (selectedEcosysstem && selectedEcosysstem!.id) {
            grantsFilter = grantsFilter.filter(grant => grant.project?.ecosystem.includes(selectedEcosysstem!.id));
        }

        if (selectedGrantsType && selectedGrantsType!.id) {
            grantsFilter = grantsFilter.filter(grant => grant.categories.includes(selectedGrantsType!.id));
        }

        if (selectedReward && selectedReward!.id) {
            grantsFilter = grantsFilter.filter(grant => grant.reward === selectedReward.id);
        }

        if (selectedProgram) {
            grantsFilter = grantsFilter.filter(
                grant => grant.project?.assetCode.toLowerCase() === selectedProgram.toLowerCase(),
            );
        }

        if (selectedProjectCategory && selectedProjectCategory?.id) {
            grantsFilter = grantsFilter.filter(grant =>
                grant.project?.categories.includes(selectedProjectCategory!.id),
            );
        }

        if (searchTerm) {
            grantsFilter = grantsFilter.filter(grant => {
                return (
                    grant.title.toLowerCase().includes(searchTerm) ||
                    grant.description.toLowerCase().includes(searchTerm) ||
                    grant.project?.title.toLowerCase().includes(searchTerm) ||
                    grant.project?.assetCode.toLowerCase().includes(searchTerm)
                );
            });
        }

        setFilteredGrants(grantsFilter);

        track('Grants.Filter', {
            ecosystem: selectedEcosysstem?.id,
            category: selectedProjectCategory?.id,
            type: selectedGrantsType?.id,
            searchTerm: searchTerm || '',
        });
    }, [
        individualGrants,
        selectedEcosysstem,
        selectedProjectCategory,
        selectedReward,
        selectedGrantsType,
        searchTerm,
        track,
        selectedProgram,
    ]);

    const featured: IndividualGrant[] = React.useMemo(() => filteredGrants.filter(g => g.featured), [filteredGrants]);

    return (
        <main className="border-gray-200 w-full">
            <section className="flex flexRow flexWrap xl:flex-nowrap overflow-hidden">
                <div className="h-full p-0 xl:borderR border-b xl:border-b-0 w-full">
                    <div className="flex flex-col space-y-8 p-4 md:p-6 lg:p-10">
                        <GrantsHeader
                            title={title}
                            breadcrumb={[{ title: 'grants', link: '/grants' }]}
                            counts={counts}
                        />
                        <div className="flex flex-col justify-start items-start self-stretch gap-2">
                            <div className="flex flex-col xs:flex-row w-full justify-start items-start self-stretch relative gap-2">
                                <div className="flex-grow w-full md:w-2/4 lg:w-2/3">
                                    <label htmlFor="searchInput" className="text-sm text-left text-gray-700">
                                        Search
                                    </label>
                                    <input
                                        id="searchInput"
                                        type="text"
                                        onChange={ev => {
                                            setSearchTerm(ev.target.value.toLowerCase());
                                            setPage(0);
                                        }}
                                        placeholder="Search..."
                                        className="w-full min-w-0 appearance-none rounded-none border border-gray-200 hover:border-gray-300 bg-white py-2.5 px-4 text-sm text-left text-gray-900 placeholder-gray-400 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                                    />
                                </div>
                                <div className="flex flex-col justify-start relative gap-1 w-full md:w-2/4 lg:w-1/3">
                                    <SingleSelectDropdown
                                        options={ecosystemOptions}
                                        label="Ecosystem"
                                        placeholder="Select ecosystem to filter"
                                        onChange={setSelectedEcosystem}
                                        value={selectedEcosysstem}
                                        className="min-w-24 "
                                    />
                                </div>
                            </div>
                            <div className="flex justify-start flex-wrap lg:flex-nowrap items-start self-stretch gap-2.5">
                                <div className="flex flex-col justify-start items-start flex-grow gap-1 w-3/8 lg:w-2/5">
                                    <div className="flex flex-col justify-start items-start self-stretch relative gap-1">
                                        <SingleSelectDropdown
                                            options={grantsTypes}
                                            label="Grants Type"
                                            placeholder="Select grants type to filter"
                                            onChange={setSelectedGrantsType}
                                            value={selectedGrantsType}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col justify-start items-start flex-grow gap-1 w-3/8 lg:w-2/5">
                                    <div className="flex flex-col justify-start items-start self-stretch relative gap-1">
                                        <SingleSelectDropdown
                                            options={categories}
                                            label="Project Category"
                                            placeholder="Select grants type to filter"
                                            onChange={setSelectedProjectCategory}
                                            value={selectedProjectCategory}
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col justify-start items-start flex-grow gap-1 w-2/8 lg:w-1/5">
                                    <div className="flex flex-col justify-start items-start self-stretch relative gap-1">
                                        <SingleSelectDropdown
                                            options={[
                                                { name: '$', id: '$' },
                                                { name: '$$', id: '$$' },
                                                { name: '$$$', id: '$$$' },
                                                { name: '$$$$', id: '$$$$' },
                                            ]}
                                            label="Rewards"
                                            placeholder="Select rewards range to filter"
                                            onChange={setSelectedReward}
                                            value={selectedReward}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col w-full">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr className="text-sm text-left uppercase text-[#6a6a6a]">
                                        <td
                                            className="px-0.5 md:px-1 pb-4 cursor-pointer select-none"
                                            onClick={() => requestSort('project', 'title')}
                                        >
                                            <div className="flex gap-1 items-center">
                                                <span className="hidden xs:block">Project</span>
                                                <span className="block xs:hidden">Proj.</span>
                                                {sortConfig?.key === 'project' && sortConfig?.secondKey === 'title' && (
                                                    <SortChevron direction={sortConfig.direction} />
                                                )}
                                            </div>
                                        </td>
                                        <td
                                            className="px-1 pb-4 cursor-pointer select-none"
                                            onClick={() => requestSort('title')}
                                        >
                                            <div className="flex gap-1 items-center">
                                                <span>Grant Details</span>
                                                {sortConfig?.key === 'title' && (
                                                    <SortChevron direction={sortConfig.direction} />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-1 pb-4 w-2/12 hidden lg:table-cell">Type</td>
                                        <td className="px-1 pb-4 w-1/12 hidden lg:table-cell">Category</td>
                                        <td
                                            className="px-1 pb-4 w-1/12 hidden lg:table-cell cursor-pointer select-none"
                                            onClick={() => requestSort('deadline')}
                                        >
                                            <div className="flex gap-1 items-center">
                                                <span>Deadline</span>
                                                {sortConfig?.key === 'deadline' && (
                                                    <SortChevron direction={sortConfig.direction} />
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-1 pb-4 w-2/12"></td>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {sortedAndFiltered.length === 0 && (
                                        <tr key="no-grants-found">
                                            <td
                                                colSpan={5}
                                                className="mb-4 text-light-gray py-4 px-2 text-sm font-semibold"
                                            >
                                                There were no grants found matching your filter criteria.
                                            </td>
                                        </tr>
                                    )}

                                    {featured.map(item => (
                                        <IndividualGrantsRow
                                            key={item.title}
                                            assets={ecosystems}
                                            item={item}
                                            onSelectGrantsType={type =>
                                                setSelectedGrantsType({
                                                    id: type,
                                                    name: type,
                                                })
                                            }
                                            onSelectCategory={category =>
                                                setSelectedProjectCategory({
                                                    id: category,
                                                    name: category,
                                                })
                                            }
                                            openPopup={(t, project, iframeUrl) =>
                                                setDialogData({ title: t, project, iframeUrl, isOpen: true })
                                            }
                                            featured
                                        />
                                    ))}

                                    {sortedAndFiltered
                                        .filter(g => !g.featured)
                                        .map(item => (
                                            <IndividualGrantsRow
                                                key={item.title}
                                                assets={ecosystems}
                                                item={item}
                                                onSelectGrantsType={type =>
                                                    setSelectedGrantsType({
                                                        id: type,
                                                        name: type,
                                                    })
                                                }
                                                onSelectCategory={category =>
                                                    setSelectedProjectCategory({
                                                        id: category,
                                                        name: category,
                                                    })
                                                }
                                                openPopup={(t, project, iframeUrl) =>
                                                    setDialogData({ title: t, project, iframeUrl, isOpen: true })
                                                }
                                            />
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <NewsRecirculationSection
                        algoliaSearchKeyword="grants"
                        title="grants in the news"
                        count={4}
                        paddingClassName="p-6"
                        className="p-0"
                    />
                </div>
            </section>
            {dialogData?.title && dialogData?.iframeUrl && dialogData?.isOpen && (
                <GrantsPopupDialog
                    title={
                        <>
                            <span className="font-semibold">{dialogData!.project}</span>: {dialogData!.title}
                        </>
                    }
                    url={dialogData!.iframeUrl}
                    isOpen={dialogData!.isOpen}
                    onClose={() => setDialogData(null)}
                    height={850}
                    width={1080}
                />
            )}
        </main>
    );
};

export default GrantsPageLayout;
